import React from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveProjectDetailsConfiguration,
} from "@planzone/view-models";
import { useRoles } from "@scenes/safeguard";
import { Language } from "@common/language";
import { Props } from "./types";
import { BudgetSection } from "./budgetSection";
import { ActivityMonitoringSection } from "./activityMonitoringSection";
import { WorkloadSection } from "./workloadSection";
import styles from "./styles.scss";
import classNames from "classnames";

const fixedColumns = [
  "weather",
  "strategicScore",
  "activityMonitoring",
  "projectManagers",
  // "activityMonitoring_done",
  // "activityMonitoring_delayed",
  // "activityMonitoring_opened",
  "workload",
  // "workload_allocatedTime",
  // "workload_spentTime",
  // "workload_remainingWork",
  "budget",
  // "budget_plannedBudget",
  // "budget_realCosts",
  // "budget_acquiredValue",
];

function SecondaryCriteria({ project, criteriaForms }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project.uuid,
    "project",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const { configuration } = useRetrieveProjectDetailsConfiguration(planzone);
  const roles = useRoles({ project });
  const profile =
    configuration.find((p) =>
      p.roles.find((role) =>
        roles.find((r) => r.uuid === role || r.name === role),
      ),
    ) || configuration.length > 0
      ? configuration[0]
      : {
          name: "",
          roles: [],
          elements: [],
        };
  const columns = profile.elements.filter(
    ({ key }) =>
      !![...fixedColumns, ...template.map(({ uuid }) => uuid)].find(
        (criteria) => criteria === key,
      ),
  );

  const getCriteriaLabel = (key: string, currentLanguage: string) => {
    const criteria = template.find(({ uuid }) => uuid === key);
    return criteria
      ? criteria.labels[currentLanguage as keyof typeof criteria.labels] ||
          criteria.name
      : t(`projectSection.columnNames.${key}`, key);
  };
  const isRequired = (key: string) => {
    const criteria = template.find(({ uuid }) => uuid === key);

    return !!criteria && criteria.attr.required === "true";
  };

  return (
    <Language>
      {({ currentLanguage }) => (
        <div className={styles.secondaryCriteriaWrapper}>
          {columns.map(({ key, type, elements = [] }) =>
            key === "budget" ? (
              <div key={key} className={styles.specialSection}>
                <h3 className={styles.sectionTitle}>
                  {t(`projectSection.columnNames.${key}`, key)}
                </h3>
                <BudgetSection project={project} />
              </div>
            ) : key === "activityMonitoring" ? (
              <div key={key} className={styles.specialSection}>
                <h3 className={styles.sectionTitle}>
                  {t(`projectSection.columnNames.${key}`, key)}
                </h3>
                <ActivityMonitoringSection project={project} />
              </div>
            ) : key === "workload" ? (
              <div key={key} className={styles.specialSection}>
                <h3 className={styles.sectionTitle}>
                  {t(`projectSection.columnNames.${key}`, key)}
                </h3>
                <WorkloadSection project={project} />
              </div>
            ) : type === "section" ? (
              <div key={key} className={styles.normalCriteria}>
                <h3 className={styles.sectionTitle}>
                  {t(`projectSection.columnNames.${key}`, key)}
                </h3>
                <div className={styles.secondaryCriteria}>
                  {elements.map((element) => (
                    <div key={element.key} className={styles.row}>
                      <div className={styles.label}>
                        <p>{getCriteriaLabel(element.key, currentLanguage)}</p>
                      </div>
                      <div className={styles.form}>
                        {
                          criteriaForms[
                            element.key as keyof typeof criteriaForms
                          ]
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div key={key} className={styles.normalCriteria}>
                <div className={styles.secondaryCriteria}>
                  <div className={styles.row}>
                    <div className={styles.label}>
                      <p
                        className={classNames({
                          [styles.required]: isRequired(key),
                        })}
                      >
                        {getCriteriaLabel(key, currentLanguage)}
                      </p>
                    </div>
                    <div className={styles.form}>
                      {criteriaForms[key as keyof typeof criteriaForms]}
                    </div>
                  </div>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </Language>
  );
}

export { SecondaryCriteria };
